import { apiHosts } from "@/api/constants/apiHosts";
import { LE } from "@/constants/enums";
import type { DocumentNew } from "@modernary/mui/components/document";
import { ToastOptionsSuccess, type ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import type { DustPermit, DustPermitNew, DustPermitVersion } from "./dustPermit";
import dustPermitService from "./dustPermitService";

type VersionInputSource = Partial<DustPermitVersion>; // | PermitNew | Partial<Permit>

const URL = "permits";
const ENTITY_NAME = "Permit";
const versionsUrl = (permitId: string | number) => `${URL}/${permitId}/versions`;

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};

const api = apiService<DustPermit, Paging<DustPermit>>(apiHosts.AQMS, axiosParams);

// /** Builds the version payload shared by permit-create and version CUD. */
export const toVersionInput = (permit: VersionInputSource) => ({
  number: permit.number,
  issueDateTime: permit.issueDateTime,
  effectiveDateTime: permit.effectiveDateTime,
  expirationDateTime: permit.expirationDateTime,
  //
  revisionTypeMurn: permit?.revisionTypeMurn,
  assignedToEmail: permit?.assignedToEmail,
  assignedTo: permit?.assignedTo,
  closedFormSubmittedDate: permit?.closedFormSubmittedDate,
  closureNotes: permit?.closureNotes,
  isConditionalRenewalIncluded: permit?.isConditionalRenewalIncluded,
  nonlinearArea: permit?.nonlinearArea,
  totalLinearAcreage: permit?.totalLinearAcreage,
  totalProjectAcreage: permit?.totalProjectAcreage,
  areaAdded: permit?.areaAdded,
  areaRemoved: permit?.areaRemoved,
  noteText: permit?.noteText,
  permitVersionsLinearSegments: permit?.permitVersionsLinearSegments,
  // pkSourceID: permit?.pkSourceID,
  // Permit create uses versionTypeMurn so it does not collide with permit typeMurn (jurisdiction).
  // Version CUD uses typeMurn on the version entity.
  versionTypeMurn:
    "versionTypeMurn" in permit
      ? (permit as DustPermitNew).versionTypeMurn
      : (permit as Partial<DustPermitVersion>).typeMurn,
});

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getDustPermits = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getDustPermitById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

//get permit by id but with a specified version filled in as the currentVersion
const getDustPermitVersionAsPermitById = (
  permitId: string | number,
  versionId: string | number,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const newUrl = versionsUrl(permitId);

  return api.get(newUrl + "/" + versionId, {
    successToastCb,
    errorToastCb,
  });
};

const getNewDustPermit = async (facilityId?: string | number) => {
  const data = dustPermitService.getInitialData() as DustPermitNew;
  return { data } as AxiosResponse<DustPermitNew, any>;
};

const createDustPermit = (permit: DustPermit, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  // Create the permit together with its initial version (category/type on the permit, the rest on the version).
  const payload = {
    constructionProjectId: permit.constructionProjectId,
    categoryMurn: permit.categoryMurn,
    typeMurn: permit.typeMurn,

    // currentVersion: toVersionInput(permit),
    ...toVersionInput(permit),
    // Initial version is always New. Forced here so a disabled Version Type field cannot
    // fall through to permit typeMurn (jurisdiction).
    versionTypeMurn: (permit as DustPermitNew).versionTypeMurn ?? LE.PermitVersionType.New,
  };

  return api.post(URL, payload, {
    successToastCb,
    errorToastCb,
  });
};

const updateDustPermit = (permit: DustPermit, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };
  // Create the permit together with its initial version (category/type on the permit, the rest on the version).
  const payload = {
    facilityId: permit.constructionProjectId,

    // facilityId: permit.facilityId,
    categoryMurn: permit.categoryMurn,
    typeMurn: permit.typeMurn,

    ...toVersionInput(permit),

    // currentVersion: toVersionInput(permit),
    // Initial version is always New. Forced here so a disabled Version Type field cannot
    // fall through to permit typeMurn (jurisdiction).
    versionTypeMurn: (permit as DustPermitNew).versionTypeMurn ?? LE.PermitVersionType.New,
  };

  return api.put(URL + "/" + permit.id, payload, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {DustPermit} permit This cannot be a 'ref' to a permit
 */
const saveDustPermit = async (permit: DustPermit, config: PagingApiAndNotifierConfig = {}) => {
  if (!permit.id) {
    return createDustPermit(permit, config);
  }

  // Editing an existing permit updates category/type only; versions are managed separately.
  return updateDustPermit(permit, config);
};

const deleteDustPermitById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const issueDustPermit = (
  request: {
    permitVersionId: number;
    issueDateTime: string;
    effectiveDateTime: string;
    expirationDateTime?: string;
    permitDocument: DocumentNew;
  },
  config: PagingApiAndNotifierConfig = {},
) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = "Permit";
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = "Permit";
    config?.errorToastCb?.(options);
  };

  return api.post(`permit-versions/${request.permitVersionId}/issue`, request, {
    successToastCb,
    errorToastCb,
  });
};

export {
  createDustPermit,
  deleteDustPermitById,
  getNewDustPermit,
  getDustPermitById,
  getDustPermits,
  getDustPermitVersionAsPermitById,
  issueDustPermit,
  saveDustPermit,
  updateDustPermit,
};
